<template>
    <div v-if="loaded">
    <!-- Detail has actions (buttons) on top which have conditional states. -->

        <!-- Row top buttons -->
        <div class="row q-mb-lg">

            <slot name="top-buttons">
                <!-- Default -->
                <div class="col-6" style="justify-content: flex-start">
                    <q-btn
                        class="capitalize ctrl-button"
                        color="grey-5"
                        :label="`${$store.state.stringsStore.strings.action.toOverview}`"
                        @click="$router.push(`/${config.module}`)"
                        icon="keyboard_backspace"
                    />
                </div>
                <div class="col-6">
                    <q-btn
                        style="float: right"
                        class="capitalize ctrl-button right q-ml-md"
                        :label="readonly ? $store.state.stringsStore.strings.action.edit : $store.state.stringsStore.strings.action.save"
                        @click="save()"
                        :color="readonly ? 'grey-5' : 'ctrl-turquoise'"
                        :icon-right="readonly ? 'edit' : 'save'"
                    />
                    <q-btn
                        v-if="$route.params.id"
                        class="q-ml-md capitalize ctrl-button right"
                        icon-right="delete"
                        color="secondary"
                        @click="deleteItem"
                        :label="$store.state.stringsStore.strings.action.delete"
                        align="right"
                    />
                </div>

            </slot>
        </div>

        <!-- Top -->
        <slot name="top"></slot>

        <!-- Mid (Default columns)-->
        <slot name="middle">
            <!-- Default Columns -->
            <div class="row gutter-sm q-mb-lg">

                <!-- Left -->
                <div class="col-md-6 col-sm-12 col-xs-12 q-pa-sm">
                    <slot name="column-left">
                        <div v-if="leftColumn">

                            <div v-for="(card, c) in leftColumn.cards" :key="c" class="full-width">
                                <p class="card-title">
                                    {{ card.title }}
                                </p>

                                <div class="card-block">
                                    <div class="row" v-for="(field, f) in card.fields" :key="f">
                                        <!-- Check allowed values for q-input types -->
                                        <template v-if="isArray(field)">
                                            <div class="col" v-for="(col, c) in field" :key="c">
                                                <component
                                                    class="full-width q-mb-md"
                                                    :is="getComponent(col.type)"
                                                    v-model="data[col.name]"
                                                    :disable="readonly"
                                                    :type="col.type"
                                                    :options="col.options"
                                                    :float-label="col.label"
                                                    :color="col.color"
                                                    :label="col.label">
                                                </component>
                                            </div>
                                        </template>

                                        <component v-else class="full-width q-mb-md"
                                          :is="getComponent(field.type)"
                                          v-model="data[field.name]"
                                          :disable="readonly"
                                          :type="field.type"
                                          :color="field.color"
                                          :options="field.options"
                                          :float-label="field.label"
                                          :label="field.label">
                                        </component>
                                    </div>
                                </div>

                            </div>
                        </div>
                    </slot>
                </div>

                <!-- Right -->
                <div class="col-md-6 col-sm-12 col-xs-12 q-pa-sm">
                    <slot name="column-right">
                        <div v-if="rightColumn">

                            <div v-for="(card, c) in rightColumn.cards" :key="c" class="full-width">
                                <p class="card-title">
                                    {{ card.title }}
                                </p>

                                <div class="card-block">
                                    <div class="row" v-for="(field, f) in card.fields" :key="f">
                                        <!-- Check allowed values for q-input types -->
                                        <template v-if="isArray(field)">
                                            <div class="col" v-for="(col, c) in field" :key="c">
                                                <component
                                                    class="full-width q-mb-md"
                                                    :is="getComponent(col.type)"
                                                    v-model="data[col.name]"
                                                    :disable="col.disable || readonly"
                                                    :type="col.type"
                                                    :color="col.color"
                                                    :options="col.options"
                                                    :float-label="col.label"
                                                    :label="col.label">
                                                </component>
                                            </div>
                                        </template>

                                        <component v-else class="full-width q-mb-md"
                                          :is="getComponent(field.type)"
                                          v-model="data[field.name]"
                                          :disable="field.disable || readonly"
                                          :type="field.type"
                                          :color="field.color"
                                          :options="field.options"
                                          :float-label="field.label"
                                          :label="field.label">
                                        </component>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </slot>
                </div>
            </div>
        </slot>

        <!-- Bottom -->
        <slot name="bottom"></slot>

    </div>
</template>

<script>
/* eslint-disable vue/no-unused-components */
import getString from '@/helpers/GetStrings';
import TextField from './inputs/TextField.vue';

export default {
  props: {
    config: {
      type: Object,
      default: () => {},
    },
  },

  components: {
    'text-input': TextField,
  },

  data() {
    return {
      readonly: true,
      data: {},
      endpoint: '',
      responseData: {
        single: '',
        multiple: '',
      },
      loaded: false,
    };
  },

  methods: {
    getString,

    isArray(a) {
      return Array.isArray(a);
    },

    getComponent(fieldType) {
      if (['text', 'textarea', 'email', 'tel', 'number', 'password', 'url', 'search'].includes(fieldType)) {
        return 'q-input';
      }
      if (fieldType === 'toggle') {
        return 'q-toggle';
      }
      if (fieldType === 'select') {
        return 'q-select';
      }
      return null;
    },

    deleteItem() {
      // Dialog
      this.$q.dialog({
        title: '',
        message: getString('upload.delete.text').replace('%file%', this.$store.state.stringsStore.language === 'en' ? 'item' : 'deze item'),
        ok: getString('dialog.confirm.ok'),
        cancel: getString('dialog.confirm.cancel'),
      }).then(() => {
        // delete call
        this.axios.delete(`${this.config.endpoint}/${this.data.id}`)
          .then(() => {
            // Refresh data to see the result
            this.$q.notify({
              message: getString('module.events.edit.delete.single'),
              position: 'top-right',
              timeout: 1000,
              color: 'positive',
            });
            this.$router.push(`/${this.config.module}`);
          })
          .catch((error) => {
            // Error
            console.warn(error);
          });
      }).catch(() => {
        console.warn('Not removed');
      });
    },

    save() {
      if (this.readonly === true) {
        this.readonly = false;
        return;
      }
      this.$emit('save', this.data);
      this.readonly = !this.readonly;
    },
  },

  computed: {
    leftColumn() {
      return this.config.columns && this.config.columns.left;
    },
    rightColumn() {
      return this.config.columns && this.config.columns.right;
    },
  },

  beforeMount() {
    this.responseData = this.config.responseData;
  },

  mounted() {
    if (!this.$route.params.id) {
      this.readonly = false;
    }
    this.data = this.config.data;
    this.loaded = true;
  },
};
</script>
